import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useHistory, withRouter } from 'react-router-dom';
import {
  useCreateVideo,
  useEncodeVideo,
  usePresignedUrl,
  useS3Upload,
} from 'lib/hooks';
import { theme } from 'lib/style';
import { initLibrary, useGlobalDispatch } from 'lib/context';
import { MoonLoader } from 'react-spinners';
import { Circle } from 'rc-progress/lib';
import { useAuth } from '../../../lib/context';
import { FILE_EXTENSION, CONTENT_TYPE } from 'lib/hooks/useMediaRecorder';
import { putVideoTags } from 'lib/api/videoTagsApi';
import { putVideoAttributes } from 'lib/api/attributesApi';
import { createVideoRequest } from 'lib/api';
import { VideoPages } from 'lib/const';
import { checkIfFreemiumAndInvalidateForCheckList } from 'lib/utils/functions';
import { useQueryClient } from 'react-query';

const FlexCenter = styled.div`
  margin-top: 16px;
  background-color: #f6f7f9;
  border-radius: 4px;
  padding: 32px;
  height: 168px;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const CircleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

type TextProps = {
  isActive?: boolean;
};

const CircleText = styled.div<TextProps>`
  display: flex;
  flex-direction: column;
  &:nth-child(2) {
    align-items: center;
    justify-content: space-between;
  }
  &:nth-child(3) {
    align-items: flex-end;
    justify-content: flex-end;
  }
  span {
    top: 0px;
    font-size: 16px;
    line-height: 1.5;
    color: ${props =>
      props.isActive ? theme.palette.black_1_100 : 'rgba(39, 42, 50, 0.4)'};
    margin-bottom: 8px;
  }
  .checkmark {
    border-radius: 20px;
    position: absolute;
    top: 106px;
    z-index: 4;
    color: white;
    background-color: ${props => props.theme.colors.primaryColor};
  }
  .rc-progress-circle {
    background-color: #f6f7f9;
  }
  & > div {
    background-color: #f6f7f9;
    z-index: 3;
    border-radius: 50px;
    position: absolute;
    top: 105px;
  }
`;

const ContentBody = styled.div`
  ${theme.fontNormal400}
  font-size: ${theme.fontSizes.m};
  color: ${theme.palette.blackRgb75};
  margin: 0;
  width: 100%;
  margin-bottom: 32px;
  min-height: 190px;
  position: relative;
`;
type ProgressBarProps = {
  color: string;
};
const ProgressBar = styled.progress<ProgressBarProps>`
  z-index: 1;
  margin-top: -14.5px;
  width: 100%;
  background-color: ${theme.palette.lightgray};
  border: none;
  border-radius: 15px;
  height: 4px;
  &::-webkit-progress-bar {
    margin: 0 auto;
    background-color: ${theme.palette.lightgray};
    border-radius: 15px;
    height: 4px;
  }
  &::-moz-progress-bar {
    margin: 0 auto;
    background-color: ${({ color }) => color};
    border-radius: 15px;
    height: 4px;
  }
  &::-webkit-progress-value {
    float: left;
    height: 4px;
    margin: 0px -10px 0 0;
    background: ${({ color }) => color};
    border-radius: 15px;
  }
`;
const ProgressText = styled.div`
  width: 100%;
  text-align: left;
  margin-bottom: 16px;
  height: 24px;
  font-size: 16px;
  line-height: 1.5;
  color: #4e5461;
  margin-top: 32px;
`;

const circleStyle = {
  width: '24px',
  zIndex: 2,
  backgroundColor: 'f6f7f9',
};

enum View {
  UPLOADING = 1,
  CREATE = 2,
  ENCODE = 3,
}

export const Upload = withRouter((props: any) => {
  const { dispatch } = useGlobalDispatch();
  const {
    file,
    fileName,
    videoTags,
    videoAttributesValues,
    attributesOrder,
    videoRequestData,
    continueToSendShare = false,
    isFreemium = false,
    selectedFolder = '',
  } = props;
  const [uploadProgress, setUploadProgress] = React.useState({
    loaded: 0,
    total: 0,
    percentage: 0,
  });
  const [progressState, setProgress] = React.useState(View.UPLOADING);
  const history = useHistory();
  const queryClient = useQueryClient();

  const { userData } = useAuth();
  const themes = useTheme();

  const generateName = (fileExt: string) => {
    return (
      userData.userId +
      '_' +
      userData.customerId +
      '_' +
      (Math.random() + 1).toString(36).substring(2) +
      '.' +
      fileExt
    );
  };

  if (!file.name) {
    file.name = generateName(FILE_EXTENSION);
  }

  const {
    presignedUrl,
    fetch: getPresignedUrl,
    loading: isLoadingUrl,
    error: urlError,
  } = usePresignedUrl();
  const {
    uploadVideo,
    loading: isUploading,
    data: uploadData,
    error: uploadError,
  } = useS3Upload();
  const {
    fetch: createVideo,
    loading: isCreating,
    videoData,
    error: createError,
    progress: createProgress,
  } = useCreateVideo(isFreemium);
  const {
    fetch: encodeVideo,
    loading: isEncoding,
    encodeData,
    progress: encodeProgress,
  } = useEncodeVideo();

  const onUploadProgress = (e: ProgressEvent) => {
    var percentCompleted = Math.round((e.loaded * 100) / e.total);
    setUploadProgress({
      loaded: Math.floor(e.loaded / 1024),
      total: Math.floor(e.total / 1024),
      percentage: percentCompleted,
    });
  };

  React.useEffect(() => {
    if (!presignedUrl) {
      return;
    }
    if (!isLoadingUrl && !urlError && file) {
      setProgress(View.UPLOADING);
      uploadVideo({
        url: presignedUrl.url,
        file,
        contentType: CONTENT_TYPE,
        onUploadProgress: onUploadProgress,
      });
    }
  }, [presignedUrl]);

  React.useEffect(() => {
    if (!isUploading && !uploadError && uploadData && file) {
      setProgress(View.CREATE);
      createVideo({
        filename: file.name,
        title: fileName,
        folder: selectedFolder,
      });
    }
  }, [isUploading, uploadData]);

  React.useEffect(() => {
    if (!isCreating && !createError && videoData && file) {
      setProgress(View.ENCODE);
      encodeVideo({
        videoId: videoData.id,
        customerId: userData.customerId,
      });
    }
  }, [isCreating, videoData]);

  React.useEffect(() => {
    if (!isEncoding && encodeData) {
      // initLibrary({ dispatch });
      // history.push(`/home`);
      Promise.all([
        updateVideoTags(),
        updateVideoAttributes(),
        addVideoRequest(),
      ]).then(result => {
        initLibrary({ dispatch });
        const videoRequestId = result[2];
        // invalidate onboarding query
        checkIfFreemiumAndInvalidateForCheckList(userData, queryClient);
        if (continueToSendShare && videoRequestId) {
          history.push(
            `/home/${VideoPages.SEND_AND_SHARE}/${videoData.id}/${
              isFreemium ? '?freemium=true' : ''
            }`
          );
          return;
        }
        history.push(`/home`);
      });
    }
  }, [isCreating, encodeData]);

  const updateVideoTags = async () => {
    if (!videoTags || !videoTags.length) {
      return;
    }

    await putVideoTags(videoData.id, videoTags).catch(() => {});
  };

  const addVideoRequest = async () => {
    if (!userData.isAutomotive) return 0;
    const videoRequest = {
      videoId: parseInt(videoData.id),
      ...videoRequestData,
    };
    const result = await createVideoRequest({ data: videoRequest });
    return result.videoRequestId;
  };

  const updateVideoAttributes = async () => {
    if (!videoAttributesValues || !attributesOrder) {
      return;
    }

    const attributes = attributesOrder.map((id: any, index: number) => {
      return {
        attributeId: id,
        value: videoAttributesValues[`attributes${index}`],
        videoId: parseInt(videoData.id),
      };
    });

    await putVideoAttributes(videoData.id, attributes).catch(err => err);
  };
  const handleSubmit = async () => {
    if (file && fileName) {
      getPresignedUrl({
        fileName: file && file.name ? file.name : '',
        type: file && file.type ? file.type : '',
      });
    }
  };

  React.useEffect(() => {
    if ((presignedUrl && presignedUrl.url) || !fileName) {
      return;
    }
    handleSubmit();
  }, [fileName]);

  const getPercentage = () => {
    if (!progressState) {
      return 0;
    }
    if (progressState == View.UPLOADING) {
      return (uploadProgress.percentage * 50) / 100;
    }
    if (progressState == View.CREATE) {
      return 50 + (createProgress || 0);
    }
    return 100;
  };

  const toMB = (KB: any) => {
    return (KB / 1000).toFixed(2);
  };

  return (
    <>
      <ContentBody>
        <FlexCenter>
          <CircleContainer>
            <CircleText isActive={isUploading}>
              <span>{isUploading ? 'Uploading' : 'Uploaded'}</span>
              {uploadProgress.percentage < 100 && (
                <MoonLoader
                  size={19.5}
                  color={themes.colors.primaryColor}
                  loading={true}
                />
              )}
              {(!uploadProgress.percentage ||
                uploadProgress.percentage === 100) && (
                <Circle
                  style={circleStyle}
                  percent={0}
                  trailWidth={10}
                  strokeWidth={10}
                  strokeColor={themes.colors.primaryColor}
                />
              )}
            </CircleText>
            <CircleText isActive={isCreating}>
              <span>{isCreating ? 'Creating' : 'Created'}</span>
              {createProgress < 100 && (
                <MoonLoader
                  size={19.5}
                  color={themes.colors.primaryColor}
                  loading={true}
                />
              )}
              {(!createProgress || createProgress === 100) && (
                <Circle
                  style={circleStyle}
                  percent={0}
                  trailWidth={10}
                  strokeWidth={10}
                  strokeColor={themes.colors.primaryColor}
                />
              )}
            </CircleText>
            <CircleText isActive={isEncoding}>
              <span>{isEncoding ? 'Encoding' : 'Encoded'}</span>
              {encodeProgress < 100 && (
                <MoonLoader
                  size={19.5}
                  color={themes.colors.primaryColor}
                  loading={true}
                />
              )}
              {(!encodeProgress || encodeProgress === 100) && (
                <Circle
                  style={circleStyle}
                  percent={0}
                  trailWidth={10}
                  strokeWidth={10}
                  strokeColor={themes.colors.primaryColor}
                />
              )}
            </CircleText>
          </CircleContainer>
          <ProgressBar
            value={getPercentage()}
            max={100}
            color={themes.colors.primaryColor}
          />
          <ProgressText>
            {toMB(uploadProgress.loaded) +
              ' MB/' +
              toMB(uploadProgress.total) +
              ' MB'}
          </ProgressText>
        </FlexCenter>
      </ContentBody>
    </>
  );
});
